import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { DEFAULT_DS_TITLE } from 'dragonmouse-shared/constants';
import forge from 'apis/forge';
import TopBar from './TopBar';
import TabBar from './TopBar/TabBar';
import FoundationsView from './sections/FoundationsView';
import ColorsView from './sections/ColorsView';
import TypographyView from './sections/TypographyView';
import StylesView from './sections/StylesView';
import ComponentsView from './sections/ComponentsView';
import GalleryView from './sections/GalleryView';
import GuidelinesView from './sections/GuidelinesView';
import AssetsView from './sections/AssetsView';
import HomeView from './sections/HomeView';
import PreparingView from './sections/PreparingView';
import { TokenStylesInjector } from './sections/tokens/TokenStylesInjector';
import * as S from './styles';
import * as SectionS from './sections/styles';
import { shallowEqual } from 'react-redux';
import DesignSystemLeftPanel from '../Panel/DesignSystemLeftPanel';
import { DsTabKind, VALID_DS_TAB_KINDS } from 'designSystem/types';
import { CHILDLESS_TAB_KINDS, DS_TAB_CHROME, buildTabId } from 'designSystem/util';
import { setDsTitle } from 'redux/db/actions';
import { addDsTab, setActiveDsTab, updateDsTab } from 'redux/mode/actions';
import { displayPromptModal } from 'redux/mode/util/modals';

const DesignSystemEditor = () => {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		designSystemContent,
		designSystemContentLoading,
		dsTabs,
		activeTab,
		isPreparing,
		needsMigration,
		activeDesignSystemId,
	} = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			designSystemContentLoading: r.db.loading.activeDesignSystemVersion,
			dsTabs: r.mode.dsTabs,
			activeTab: selectActiveDsTab(r.mode),
			isPreparing: r.db.dsPreparing,
			needsMigration: r.db.dsMigrating,
			activeDesignSystemId: r.mode.activeDesignSystemId,
		}),
		shallowEqual
	);

	const hasShownRenameRef = useRef(false);

	const isValidTabKind = (value: string | null): value is DsTabKind => {
		return value !== null && VALID_DS_TAB_KINDS.includes(value as DsTabKind);
	};

	const activeTabKind = activeTab?.kind || 'foundations';
	const activeContentPath = activeTab?.contentPath;

	// Sync URL with active tab kind and contentPath (only when there's an active tab).
	// The URL param is `tabKind` — its value is a DsTabKind.
	useEffect(() => {
		if (activeTab) {
			const params: Record<string, string> = { tabKind: activeTabKind };
			if (activeContentPath) {
				params.contentPath = activeContentPath;
			}
			setSearchParams(params);
		} else if (designSystemContent) {
			// Only clear URL params once content has loaded -- clearing earlier would
			// wipe incoming deep-link params before the bootstrap effect can use them.
			setSearchParams({});
		}
	}, [activeTab, activeTabKind, activeContentPath, setSearchParams, designSystemContent]);

	// Initialize tabs from URL on mount if URL param doesn't match current active tab.
	// Accept the legacy `?section=` param as a fallback so old bookmarks still work —
	// the URL-sync effect above will rewrite to `?tabKind=` on the next render.
	useEffect(() => {
		const tabKindParam = searchParams.get('tabKind') ?? searchParams.get('section');
		const rawContentPathParam = searchParams.get('contentPath');
		if (!isValidTabKind(tabKindParam)) return;

		// Childless kinds can't carry a contentPath — strip it so a stale URL like
		// `?tabKind=foundations&contentPath=foo` doesn't spawn a ghost `foundations-foo` tab.
		const contentPathParam = CHILDLESS_TAB_KINDS.has(tabKindParam) ? null : rawContentPathParam;

		const tabId = buildTabId(tabKindParam, contentPathParam ?? undefined);

		// Skip if the active tab already matches the URL (avoids feedback loop with URL sync effect)
		if (activeTab?.id === tabId && activeTab?.contentPath === (contentPathParam || undefined))
			return;

		const existingTab = dsTabs.find((t) => t.id === tabId);
		if (existingTab) {
			// For assets, update contentPath if it changed
			if (
				tabKindParam === 'assets' &&
				contentPathParam &&
				existingTab.contentPath !== contentPathParam
			) {
				dispatch(updateDsTab({ id: tabId, contentPath: contentPathParam }));
			}
			if (existingTab.id !== activeTab?.id) {
				dispatch(setActiveDsTab(tabId));
			}
		} else {
			// Tab doesn't exist, create it with proper label
			const chrome = DS_TAB_CHROME[tabKindParam];
			let label =
				chrome?.label || tabKindParam.charAt(0).toUpperCase() + tabKindParam.slice(1);

			// If contentPath provided, verify the content actually exists before creating a tab.
			// Without this guard, stale URLs (e.g. after a rename) spawn ghost tabs for
			// content that no longer exists, which then fight the URL sync effect in a loop.
			if (contentPathParam && tabKindParam !== 'assets') {
				if (!designSystemContent) return;
				let found = false;
				if (tabKindParam === 'guidelines') {
					const guideline = designSystemContent.guidelines?.find(
						(g) => g.path === contentPathParam
					);
					if (guideline) {
						label = guideline.title;
						found = true;
					}
				} else if (tabKindParam === 'components') {
					const component = designSystemContent.components?.all?.find(
						(c) => c.id === contentPathParam
					);
					if (component) {
						label = component.name;
						found = true;
					}
				} else if (tabKindParam === 'gallery') {
					const gallery = designSystemContent.gallery?.find((g) => g.path === contentPathParam);
					if (gallery) {
						label = gallery.title;
						found = true;
					}
				}
				if (!found) return;
			}

			dispatch(
				addDsTab({
					id: tabId,
					kind: tabKindParam,
					contentPath: contentPathParam || undefined,
					label,
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- activeTab and dsTabs intentionally excluded; a separate effect syncs URL on activeTab changes
	}, [searchParams, designSystemContent, dispatch]);

	// Show rename prompt for newly created design systems
	useEffect(() => {
		if (hasShownRenameRef.current) return;
		if (searchParams.get('new') !== 'true') return;
		if (!activeDesignSystemId) return;
		hasShownRenameRef.current = true;

		const dsId = activeDesignSystemId;
		setSearchParams({}, { replace: true });
		displayPromptModal({
			title: 'Name your design system',
			placeholder: 'e.g. Acme Design System',
			submitLabel: 'Save',
			initialValue: DEFAULT_DS_TITLE,
			validate: (v: string) => (v.trim() ? null : 'Name cannot be empty'),
			onSubmit: (value) => {
				if (!value) return;
				const trimmed = value.trim();
				// Title bar / HomeView read r.db.dsTitle (version poll), not the optimistic DS write.
				dispatch(setDsTitle(trimmed));
				forge.updateDesignSystem({ update: { id: dsId, title: trimmed } });
			},
		});
	}, [searchParams, activeDesignSystemId, setSearchParams, dispatch]);

	// Trigger one-time migration when the polling stream detects an unmigrated DS
	const migrationTriggeredRef = useRef<string | null>(null);
	useEffect(() => {
		if (!activeDesignSystemId || !needsMigration) return;
		if (migrationTriggeredRef.current === activeDesignSystemId) return;
		migrationTriggeredRef.current = activeDesignSystemId;

		forge.migrateDesignSystem(activeDesignSystemId).catch((error: unknown) => {
			Sentry.captureException(error);
		});
	}, [activeDesignSystemId, needsMigration]);

	const renderSectionView = () => {
		// Show PreparingView when DS is being prepared for first time
		if (isPreparing) {
			return <PreparingView />;
		}

		// Show HomeView when no active tab
		if (!activeTab) {
			return <HomeView />;
		}

		// Show loading state while DS content is being loaded
		if (designSystemContentLoading === 'loading') {
			return (
				<SectionS.SectionBase>
					<SectionS.SectionTitle>Loading Design System...</SectionS.SectionTitle>
					<SectionS.SectionContent>
						<p>Loading design system content...</p>
					</SectionS.SectionContent>
				</SectionS.SectionBase>
			);
		}

		// Show empty state if content hasn't loaded yet
		if (designSystemContentLoading === 'never-loaded' || !designSystemContent) {
			return (
				<SectionS.SectionBase>
					<SectionS.SectionTitle>Design System</SectionS.SectionTitle>
					<SectionS.SectionContent>
						<p>
							No design system content available. If you think this is an error, please contact
							hello@moonchild.ai for assistance.
						</p>
					</SectionS.SectionContent>
				</SectionS.SectionBase>
			);
		}

		switch (activeTabKind) {
			case 'foundations':
				return <FoundationsView />;
			case 'colors':
				return <ColorsView />;
			case 'typography':
				return <TypographyView />;
			case 'styles':
				return <StylesView />;
			case 'components':
				return <ComponentsView />;
			case 'gallery':
				return <GalleryView />;
			case 'guidelines':
				return <GuidelinesView />;
			case 'assets':
				return <AssetsView />;
			default:
				return <FoundationsView />;
		}
	};

	return (
		<S.Container>
			<TokenStylesInjector />
			<S.TopBarWrapper>
				<TopBar />
			</S.TopBarWrapper>
			<S.ContentWrapper>
				<S.LeftPanel>
					<DesignSystemLeftPanel />
				</S.LeftPanel>
				<S.MainContent>
					{!isPreparing && <TabBar />}
					<S.SectionContentWrapper>{renderSectionView()}</S.SectionContentWrapper>
				</S.MainContent>
				<S.RightPanel>{/* Reserved for detail panel */}</S.RightPanel>
			</S.ContentWrapper>
		</S.Container>
	);
};

export default DesignSystemEditor;
